import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Global event bus for tooltip groups — only one tooltip open per group
const tooltipBus = {
  listeners: {},
  emit(group, id) {
    if (group && this.listeners[group]) {
      this.listeners[group].forEach(fn => fn(id));
    }
  },
  on(group, fn) {
    if (!group) return () => {};
    if (!this.listeners[group]) this.listeners[group] = new Set();
    this.listeners[group].add(fn);
    return () => this.listeners[group]?.delete(fn);
  }
};

let tooltipIdCounter = 0;

const GameTooltip = ({ text, children, position = 'bottom', group = null }) => {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const timerRef = useRef(null);
  const triggerRef = useRef(null);
  const idRef = useRef(++tooltipIdCounter);
  const tooltipRef = useRef(null);

  // Subscribe to group bus — close when another tooltip in group opens
  useEffect(() => {
    if (!group) return;
    const unsub = tooltipBus.on(group, (activeId) => {
      if (activeId !== idRef.current) {
        if (timerRef.current) clearTimeout(timerRef.current);
        setVisible(false);
        setFading(false);
      }
    });
    return unsub;
  }, [group]);

  // Recalculate position when visible
  useEffect(() => {
    if (!visible || !triggerRef.current) return;
    const updatePos = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipEl = tooltipRef.current;
      const tooltipWidth = tooltipEl?.offsetWidth || 200;
      const tooltipHeight = tooltipEl?.offsetHeight || 40;
      let top, left;

      if (position === 'top') {
        top = rect.top - tooltipHeight - 8;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
      } else {
        top = rect.bottom + 8;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
      }

      // Keep within viewport
      const pad = 8;
      if (left < pad) left = pad;
      if (left + tooltipWidth > window.innerWidth - pad) left = window.innerWidth - pad - tooltipWidth;
      if (top < pad) {
        top = rect.bottom + 8; // flip to bottom if top clips
      }

      setCoords({ top, left });
    };
    // Run twice: once immediately, once after portal renders with measured width
    updatePos();
    requestAnimationFrame(updatePos);
  }, [visible, position]);

  const show = useCallback((e) => {
    e.stopPropagation();
    if (timerRef.current) clearTimeout(timerRef.current);
    if (group) tooltipBus.emit(group, idRef.current);
    setFading(false);
    setVisible(true);
    timerRef.current = setTimeout(() => {
      setFading(true);
      timerRef.current = setTimeout(() => {
        setVisible(false);
        setFading(false);
      }, 500);
    }, 3000);
  }, [group]);

  const tooltip = visible
    ? createPortal(
        <div
          ref={tooltipRef}
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: coords.top,
            left: coords.left,
            opacity: fading ? 0 : 1,
            transition: 'opacity 0.5s ease',
          }}
        >
          <div className="bg-[#2a2a40] border border-[#D4AF37]/30 rounded-lg px-4 py-2 shadow-lg max-w-[250px] whitespace-normal">
            <span className="text-[13px] text-[#F5F5F7] leading-tight">{text}</span>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={triggerRef} className="relative w-full" onClick={show}>
      {children}
      {tooltip}
    </div>
  );
};

export default GameTooltip;
